import { supabase } from "@/integrations/supabase/client";

// Generate a unique session ID
const generateSessionId = (): string => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Get or create session ID
const getSessionId = (): string => {
  const storageKey = 'visitor_session_id';
  let sessionId = sessionStorage.getItem(storageKey);
  
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(storageKey, sessionId);
  }
  
  return sessionId;
};

// Extract UTM parameters from URL
const extractUTMParams = (url: string) => {
  const urlObj = new URL(url);
  return {
    utmSource: urlObj.searchParams.get('utm_source'),
    utmMedium: urlObj.searchParams.get('utm_medium'),
    utmCampaign: urlObj.searchParams.get('utm_campaign'),
    utmTerm: urlObj.searchParams.get('utm_term'),
    utmContent: urlObj.searchParams.get('utm_content'),
  };
};

// Check if user has DNT (Do Not Track) enabled
const respectsDNT = (): boolean => {
  return navigator.doNotTrack === '1' || 
         navigator.doNotTrack === 'yes' || 
         (window as any).doNotTrack === '1';
};

// Track visitor data
export const trackVisitor = async (): Promise<void> => {
  try {
    // Respect Do Not Track preference
    if (respectsDNT()) {
      console.log('Visitor tracking skipped due to Do Not Track preference');
      return;
    }

    // Check if we've already tracked this session for this page
    const trackingKey = `tracked_${getSessionId()}_${window.location.pathname}`;
    if (sessionStorage.getItem(trackingKey)) {
      return; // Already tracked this session/page combination
    }

    const utmParams = extractUTMParams(window.location.href);
    
    const visitorData = {
      sessionId: getSessionId(),
      landingPage: window.location.href,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      ...utmParams,
      timestamp: new Date().toISOString()
    };

    // Call the edge function to track visitor
    const { error } = await supabase.functions.invoke('track-visitor', {
      body: { visitorData }
    });

    if (error) {
      console.error('Error tracking visitor:', error);
    } else {
      // Mark this session/page as tracked
      sessionStorage.setItem(trackingKey, 'true');
      console.log('Visitor tracked successfully');
    }

  } catch (error) {
    console.error('Error in visitor tracking:', error);
  }
};

// Track visitor on page load
export const initVisitorTracking = (): void => {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackVisitor);
  } else {
    trackVisitor();
  }
};